import React from 'react';
import {
  RequestActivityDownloadData,
  RequestActivityEvent,
  requestActivityEvent,
  RequestActivityResponse,
  requestDownloadSource,
  RequestActivityStatusChangeData,
  RequestActivityType,
  requestActivityType,
} from 'src/lib/services/api/request-api';
import { Avatar, AvatarFallback, AvatarImage } from 'src/components/ui/avatar';
import { str } from 'src/lib/utils';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import { useFormatter } from 'src/services/i18n';
import { RequestStatusBadge } from 'src/features/requests/request-status-badge';
import { RequestStatusDetails } from 'src/features/requests/request-table/request-status-details';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { appRoutes } from 'src/routes';
import { Skeleton } from 'src/components/ui/skeleton';
import { AppLogoShort } from 'src/components/ui/app-logo-short';
import { roles } from 'src/api/services/RoleClient';
import { RenderForAdmin } from 'src/components/Middleware';
import { Download } from 'lucide-react';

const defaultActivityMessages: Record<RequestActivityType, Record<RequestActivityEvent, string>> = {
  [requestActivityType.status]: {
    [requestActivityEvent.created]: 'changed request status to:', // shouldn't happen, defining because typescript
    [requestActivityEvent.updated]: 'changed request status to:',
    [requestActivityEvent.deleted]: 'changed request status to:', // shouldn't happen, defining because typescript
  },
  [requestActivityType.approval]: {
    [requestActivityEvent.created]: 'changed request sub-status to:',
    [requestActivityEvent.updated]: 'changed request sub-status to:',
    [requestActivityEvent.deleted]: 'removed sub-status "approved - pending resizes"',
  },
  [requestActivityType.reedit]: {
    // #2533: Needs Re-edit is surfaced as a top-tier status change, not a
    // nested sub-status — the request's effective status IS "Needs Re-edit".
    // The paired Status->Queued activity is suppressed back-end side
    // (RequestObserver) so this is the single line for the transition.
    [requestActivityEvent.created]: 'changed request status to:',
    [requestActivityEvent.updated]: 'changed request status to:',
    [requestActivityEvent.deleted]: 'removed status "needs re-edit"',
  },
  [requestActivityType.attention]: {
    // #2533: Needs Attention is a top-tier status, not a nested sub-status —
    // log and render it the same way as the other request statuses.
    [requestActivityEvent.created]: 'changed request status to:',
    [requestActivityEvent.updated]: 'changed request status to:',
    [requestActivityEvent.deleted]: 'removed status "needs attention"',
  },
  // #2784. Only `created` is ever written; the message itself is built in
  // RequestActivityEntryMessage from the row's data (file count, zip, link).
  [requestActivityType.download]: {
    [requestActivityEvent.created]: 'downloaded',
    [requestActivityEvent.updated]: 'downloaded',
    [requestActivityEvent.deleted]: 'downloaded',
  },
};

const isDownloadActivity = (
  activity: RequestActivityResponse,
): activity is RequestActivityResponse & { data: RequestActivityDownloadData } =>
  activity.type === requestActivityType.download && !!activity.data;

type Props = {
  activity: RequestActivityResponse;
};

const RequestActivityEntry: React.FC<Props> = ({ activity }) => {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatter();

  const downloadedBy = isDownloadActivity(activity) ? activity.data.downloaded_by : null;

  const fullName = activity.user
    ? joinExistingValues([activity.user.first_name, activity.user.last_name], ' ') ||
      activity.user.email
    : downloadedBy
      ? // The user was deleted since; the row kept who it was.
        downloadedBy.name || downloadedBy.email || ''
      : isDownloadActivity(activity) && activity.data.via === requestDownloadSource.shareLink
        ? // A public share link: whoever opened it is anonymous.
          t('pages/admin:request.request_activity.download.share_link_visitor', 'Someone')
        : t('common:system', 'System');

  return (
    <div className={'tw-flex tw-gap-4'}>
      <div>
        <RequestActivityUserAvatar user={activity.user} />
      </div>

      <div className={'tw-flex tw-flex-col tw-gap-2'}>
        <div>
          <span className={'tw-text-lg tw-font-medium'}>{fullName}</span>{' '}
          <RequestActivityEntryMessage activity={activity} />
        </div>

        <RequestActivityEntryValue activity={activity} />

        <TooltipProvider>
          <Tooltip>
            <div className={'tw-flex tw-text-sm tw-font-normal tw-text-text-subtle'}>
              <TooltipTrigger asChild>
                <div>
                  {formatDateTime(activity.created_at, {
                    weekday: 'long',
                    month: 'long',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </TooltipTrigger>
            </div>

            <TooltipContent>{formatDateTime(activity.created_at)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

const RequestActivityUserAvatar: React.FC<{ user: RequestActivityResponse['user'] }> = ({
  user,
}) => {
  return user ? (
    <RenderForAdmin
      and={[roles.teamLead]}
      else={
        <Avatar size={'md'}>
          <AvatarImage src={user.avatar?.index_url} alt={user.first_name} />
          <AvatarFallback className={'tw-bg-orange tw-text-white'}>
            {str.acronym(joinExistingValues([user.first_name, user.last_name], ' ')).slice(0, 2)}
          </AvatarFallback>
        </Avatar>
      }
    >
      <Link to={generatePath(appRoutes.user, { userId: user?.id })}>
        <Avatar size={'md'}>
          <AvatarImage src={user.avatar?.index_url} alt={user.first_name} />
          <AvatarFallback className={'tw-bg-orange tw-text-white'}>
            {str.acronym(joinExistingValues([user.first_name, user.last_name], ' ')).slice(0, 2)}
          </AvatarFallback>
        </Avatar>
      </Link>
    </RenderForAdmin>
  ) : (
    <Avatar size={'md'} className={'tw-items-center tw-justify-center'}>
      <AppLogoShort width={24} />
    </Avatar>
  );
};

const RequestActivityEntryMessage: React.FC<Props> = ({ activity }) => {
  const { t } = useTranslation();

  if (isDownloadActivity(activity)) {
    const count = activity.data.files?.length ?? 0;

    switch (activity.data.via) {
      case requestDownloadSource.zip:
        return (
          <>
            {count === 1
              ? t('pages/admin:request.request_activity.download.zip_one', {
                  defaultValue: 'downloaded 1 file as a zip',
                })
              : t('pages/admin:request.request_activity.download.zip_many', {
                  defaultValue: 'downloaded {{count}} files as a zip',
                  count,
                })}
          </>
        );
      case requestDownloadSource.shareLink:
        return (
          <>
            {t('pages/admin:request.request_activity.download.share_link', {
              defaultValue: 'downloaded a file via a share link',
            })}
          </>
        );
      default:
        return (
          <>
            {t('pages/admin:request.request_activity.download.file', {
              defaultValue: 'downloaded a file',
            })}
          </>
        );
    }
  }

  return (
    <>
      {t(`pages/admin:request.request_activity.${activity.type}.${activity.event}`, {
        // Null-safe: an activity type this build doesn't know yet (the API
        // can ship one before the admin does) must not crash the whole log.
        defaultValue: defaultActivityMessages[activity.type]?.[activity.event] ?? '',
      })}
    </>
  );
};

const RequestActivityDownloadValue: React.FC<{
  data: RequestActivityDownloadData;
  /** The company Activity Log has its own Company column, so it hides this line. */
  showCompany?: boolean;
}> = ({ data, showCompany = true }) => {
  const { t } = useTranslation();

  return (
    <div className={'tw-flex tw-flex-col tw-gap-1 tw-text-sm'}>
      <ul className={'tw-flex tw-flex-col tw-gap-0.5'}>
        {(data.files ?? []).map((file) => (
          <li key={file.media_id} className={'tw-flex tw-items-center tw-gap-1.5'}>
            <Download size={14} className={'tw-shrink-0 tw-text-text-subtle'} />
            <span className={'tw-break-all tw-font-medium'}>
              {file.file_name || t('common:unnamed_file', 'Unnamed file')}
            </span>
            {file.version ? (
              <span className={'tw-shrink-0 tw-text-text-subtle'}>
                {t('pages/admin:request.request_activity.download.version', {
                  defaultValue: 'Version {{version}}',
                  version: file.version,
                })}
              </span>
            ) : file.revision_type === 'final_files' ? (
              <span className={'tw-shrink-0 tw-text-text-subtle'}>
                {t('pages/admin:request.request_activity.download.final_files', {
                  defaultValue: 'Final Files',
                })}
              </span>
            ) : file.revision_type === 'first_contact' ? (
              <span className={'tw-shrink-0 tw-text-text-subtle'}>
                {t('pages/admin:request.request_activity.download.first_contact', {
                  defaultValue: 'First Contact',
                })}
              </span>
            ) : !file.revision_id ? (
              // Hangs off the request itself: the client's own upload, not
              // something we delivered.
              <span className={'tw-shrink-0 tw-text-text-subtle'}>
                {t('pages/admin:request.request_activity.download.own_upload', {
                  defaultValue: "(client's own upload)",
                })}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {showCompany && data.company_name ? (
        <div className={'tw-text-text-subtle'}>
          {t('pages/admin:request.request_activity.download.company', {
            defaultValue: 'Company: {{company}}',
            company: data.company_name,
          })}
        </div>
      ) : null}
    </div>
  );
};

const RequestActivityEntryValue: React.FC<{ activity: RequestActivityResponse }> = ({
  activity,
}) => {
  if (activity.event === requestActivityEvent.deleted) {
    return null;
  }

  if (isDownloadActivity(activity)) {
    return <RequestActivityDownloadValue data={activity.data} />;
  }

  const ValueComponent = () => {
    switch (activity.type) {
      case requestActivityType.status:
        return (
          <RequestStatusBadge
            variant={(activity.data as RequestActivityStatusChangeData).to}
            size={'sm'}
          />
        );
      case requestActivityType.approval:
        return <RequestStatusDetails approved={activity.created_at} size={'sm'} />;
      // #2533: Needs Re-edit / Needs Attention are top-tier statuses, so render
      // them with the same status badge as the other statuses instead of the
      // RequestStatusDetails sub-status alert pill.
      case requestActivityType.reedit:
        return <RequestStatusBadge variant={'needs_reedit'} size={'sm'} />;
      case requestActivityType.attention:
        return <RequestStatusBadge variant={'needs_attention'} size={'sm'} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <ValueComponent />
    </div>
  );
};

const RequestActivityEntrySkeleton: React.FC = () => {
  return (
    <div className={'tw-flex tw-gap-4'}>
      <Skeleton className={'tw-size-8 tw-rounded-full'} />
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Skeleton className={'tw-h-3 tw-w-60'} />
        <Skeleton className={'tw-h-3 tw-w-16'} />
        <Skeleton className={'tw-h-3 tw-w-24'} />
      </div>
    </div>
  );
};

export { RequestActivityEntrySkeleton, RequestActivityDownloadValue };
export default RequestActivityEntry;
